/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList, ShoppingCart, Plus, Search, MapPin, X, Image as ImageIcon, Eye, Check,
  History, LayoutGrid, List as ListIcon, AlertTriangle, Receipt, BookmarkPlus,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PlafondRequestDialog } from "../../components/plafond";
import { DatePickerIT } from "../../components/DatePickerIT";
import { DeliveryCard } from "../../components/logistics/DeliveryInfo";
import { DecimalInput } from "../../components/DecimalInput";
import { useLang } from "../../context/LangContext";
import { API, BACKEND_URL, useFmt } from "./_shared";
import TechSheetButton from "../../components/TechSheetButton";

/**
 * Searchable customer selector with free-text input + dropdown. Used inside the
 * new-order page to pick the customer the order is for.
 */
const CustomerSearchSelect = ({ customers, value, onChange, placeholder = "Cerca cliente..." }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  const selected = customers.find((c) => c.id === value);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const q = query.trim().toLowerCase();
  const filtered = !q ? customers.slice(0, 50)
    : customers.filter((c) =>
        (c.business_name || "").toLowerCase().includes(q) ||
        (c.vat_number || "").toLowerCase().includes(q) ||
        (c.codice_cliente || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.city || "").toLowerCase().includes(q)
      ).slice(0, 50);
  return (
    <div className="relative" ref={wrapRef} data-testid="new-order-customer-search-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
        <Input
          value={open ? query : (selected ? `${selected.codice_cliente} · ${selected.business_name}${selected.is_foreign ? " (Estero)" : ""}` : query)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onChange(""); }}
          onFocus={() => { setQuery(""); setOpen(true); }}
          placeholder={placeholder}
          className="pl-10 pr-9 bg-white border-stone-300"
          data-testid="new-order-customer-search"
        />
        {(selected || query) && (
          <button type="button" onClick={() => { onChange(""); setQuery(""); setOpen(true); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700" data-testid="new-order-customer-clear" aria-label="Pulisci">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="customer-search-dropdown">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-stone-500">Nessun cliente trovato</p>
          ) : filtered.map((c) => (
            <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); setQuery(""); }} className={`w-full text-left p-2 hover:bg-stone-50 border-b border-stone-100 last:border-b-0 ${c.id === value ? "bg-stone-50" : ""}`} data-testid={`customer-search-item-${c.codice_cliente}`}>
              <p className="text-sm font-medium text-stone-900 truncate">
                <span className="font-mono text-xs text-[#8A1C2A] mr-2">{c.codice_cliente}</span>
                {c.business_name}{c.is_foreign && <span className="ml-1 text-xs text-blue-600">(Estero)</span>}
              </p>
              <p className="text-xs text-stone-500 truncate">
                {c.vat_number ? `P.IVA ${c.vat_number} · ` : ""}{c.city || ""}{c.email ? ` · ${c.email}` : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NewOrderPage = () => {
  const { t } = useLang();
  const fm = useFmt();
  const [catalog, setCatalog] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cart, setCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [specialRequests, setSpecialRequests] = useState("");
  const [productDetail, setProductDetail] = useState(null);
  const [selectedDeliveryIdx, setSelectedDeliveryIdx] = useState("__sede__");
  const [customDeliveryAddr, setCustomDeliveryAddr] = useState({ label: "", address: "", city: "", cap: "", province: "", notes: "" });
  const [saveDeliveryToCustomer, setSaveDeliveryToCustomer] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [showRevision, setShowRevision] = useState(false);
  const [bigView, setBigView] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pricelistOverride, setPricelistOverride] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [exposure, setExposure] = useState(null);
  const [plafondReqOpen, setPlafondReqOpen] = useState(false);
  const [quickNotes, setQuickNotes] = useState([]);
  const [requestedShippingDate, setRequestedShippingDate] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [draftNumber, setDraftNumber] = useState(null);
  const draftLoadedRef = React.useRef(false);
  const skipPricelistResetRef = React.useRef(false);
  const skipNotesPrefillRef = React.useRef(false);
  const lastPricelistIdRef = React.useRef(null);
  const navigate = useNavigate();

  // Caricamento di un ordine provvisorio esistente (?draft_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const did = params.get("draft_id");
    if (!did || draftLoadedRef.current || customers.length === 0) return;
    draftLoadedRef.current = true;
    axios.get(`${API}/orders/${did}`).then((r) => {
      const o = r.data;
      if (o.status !== "draft") { toast.error("L'ordine non è più provvisorio"); return; }
      setDraftId(o.id);
      setDraftNumber(o.order_number);
      skipPricelistResetRef.current = true;
      skipNotesPrefillRef.current = true;
      setSelectedCustomerId(o.customer_id || "");
      if (o.pricelist_type_id) setPricelistOverride(o.pricelist_type_id);
      setCart((o.items || []).map((it) => ({
        product_id: it.product_id || null, code: it.code, description: it.description,
        category_id: it.category_id || null, category_name: it.category_name || "",
        quantity: it.quantity, unit: it.unit || "Kg", unit_price: it.unit_price,
        line_total: it.line_total, is_free: !!it.is_free, vat_rate: it.vat_rate ?? 0.10,
      })));
      setSpecialRequests(o.customer?.special_requests || "");
      setDeliveryNotes(o.delivery_notes || "");
      setRequestedShippingDate(o.requested_shipping_date || "");
      const cust = customers.find((c) => c.id === o.customer_id);
      const da = o.delivery_address;
      if (da && cust) {
        const idx = (cust.delivery_addresses || []).findIndex((d) => d.address === da.address && d.city === da.city);
        if (idx >= 0) setSelectedDeliveryIdx(String(idx));
        else if (da.address && da.address !== cust.address) {
          setSelectedDeliveryIdx("__new__");
          setCustomDeliveryAddr({ label: da.label || "", address: da.address || "", city: da.city || "", cap: da.cap || "", province: da.province || "", notes: da.notes || "" });
        }
      }
      toast.success(`Provvisorio ${o.order_number} caricato — modifica e invia quando vuoi`);
    }).catch(() => toast.error("Errore caricamento ordine provvisorio"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  // Note rapide riutilizzabili (per utente)
  const loadQuickNotes = () => {
    axios.get(`${API}/quick-notes`).then((r) => setQuickNotes(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadQuickNotes(); }, []);
  const saveQuickNote = async () => {
    const text = deliveryNotes.trim();
    if (!text) return;
    try {
      await axios.post(`${API}/quick-notes`, { text });
      toast.success("Nota rapida salvata — la ritroverai nei prossimi ordini");
      loadQuickNotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio nota"); }
  };
  const deleteQuickNote = async (id) => {
    try {
      await axios.delete(`${API}/quick-notes/${id}`);
      setQuickNotes((prev) => prev.filter((n) => n.id !== id));
    } catch { toast.error("Errore eliminazione nota"); }
  };

  // Situazione contabile del cliente (alert scaduto/aperto dallo scadenziario)
  useEffect(() => {
    if (!selectedCustomerId) { setExposure(null); return; }
    axios.get(`${API}/sales-documents/customer-exposure/${selectedCustomerId}`)
      .then((r) => setExposure(r.data))
      .catch(() => setExposure(null));
  }, [selectedCustomerId]);

  // Precompila le note di consegna ricordate per il cliente
  useEffect(() => {
    if (!selectedCustomerId) return;
    if (skipNotesPrefillRef.current) { skipNotesPrefillRef.current = false; return; }
    const c = customers.find((x) => x.id === selectedCustomerId);
    if (c) setDeliveryNotes(c.last_delivery_notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  const goToCustomerStatements = () => {
    if (!selectedCustomerId) return;
    navigate(`/agent/statements?customer_id=${selectedCustomerId}`);
  };

  // Pre-load offer from query string ?offer_id=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oid = params.get("offer_id");
    if (!oid) return;
    axios.get(`${API}/offers/${oid}`).then((r) => {
      const o = r.data;
      const banner = `OFFERTA ${o.title}${o.notes ? " - " + o.notes : ""}`;
      setCart([{
        product_id: o.product_id || null,
        code: o.product_code || "OFF",
        description: banner,
        category_id: o.category_id || null,
        category_name: o.category_name || "",
        quantity: 1, unit: o.unit || "Kg",
        unit_price: o.price, line_total: o.price,
        is_free: !o.product_id, vat_rate: 0.10,
        from_offer_id: o.id,
      }]);
      toast.success(`Offerta "${o.title}" aggiunta al carrello — seleziona un cliente e aggiungi altri prodotti se vuoi`);
    }).catch(() => {});
  }, []);

  const loadCatalog = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCustomerId) params.append("customer_id", selectedCustomerId);
      if (search) params.append("search", search);
      if (categoryFilter !== "all") params.append("category_id", categoryFilter);
      if (pricelistOverride) params.append("pricelist_type_id", pricelistOverride);
      const r = await axios.get(`${API}/agent/catalog?${params}`);
      setCatalog(r.data);
      // riallinea i prezzi del carrello al listino attivo
      const prods = r.data?.products || [];
      const updatedLines = cart.filter((line) => line.product_id && !line.is_free && !line.from_offer_id
        && prods.some((x) => x.id === line.product_id && x.price !== line.unit_price)).length;
      setCart((prev) => prev.map((line) => {
        if (!line.product_id || line.is_free || line.from_offer_id) return line;
        const p = prods.find((x) => x.id === line.product_id);
        if (!p || p.price === line.unit_price) return line;
        return { ...line, unit_price: p.price, line_total: +(line.quantity * p.price).toFixed(2) };
      }));
      // feedback al cambio listino (utile anche per capire se i prezzi coincidono tra listini)
      const newPl = r.data?.pricelist;
      if (lastPricelistIdRef.current && newPl?.id && lastPricelistIdRef.current !== newPl.id) {
        toast.success(`Prezzi aggiornati al listino "${newPl.name}"${updatedLines > 0 ? ` — ${updatedLines} ${updatedLines === 1 ? "riga carrello riallineata" : "righe carrello riallineate"}` : ""}`);
      }
      lastPricelistIdRef.current = newPl?.id || null;
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento catalogo"); }
  };
  useEffect(() => { axios.get(`${API}/customers`).then((r) => setCustomers(r.data || [])); }, []);
  useEffect(() => {
    const tm = setTimeout(loadCatalog, 200);
    return () => clearTimeout(tm);
  }, [selectedCustomerId, search, categoryFilter, pricelistOverride]);

  // Cambio cliente → torna al listino predefinito del cliente
  useEffect(() => {
    if (skipPricelistResetRef.current) { skipPricelistResetRef.current = false; return; }
    setPricelistOverride("");
    setSetAsDefault(false);
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomerId) { setSelectedCustomer(null); return; }
    const c = customers.find((x) => x.id === selectedCustomerId);
    setSelectedCustomer(c || null);
  }, [selectedCustomerId, customers]);

  const addToCart = (prod) => {
    setCart((prev) => {
      const idx = prev.findIndex((p) => p.product_id === prod.id);
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], quantity: c[idx].quantity + 1, line_total: +((c[idx].quantity + 1) * c[idx].unit_price).toFixed(2) };
        return c;
      }
      return [...prev, {
        product_id: prod.id, code: prod.code, description: prod.description,
        category_id: prod.category_id, category_name: prod.category_name || "",
        quantity: 1, unit: prod.unit || "Kg", unit_price: prod.price, line_total: prod.price,
        is_free: false, vat_rate: prod.vat_rate ?? 0.10,
      }];
    });
  };
  const addFreeItem = () => {
    setCart((prev) => [...prev, {
      product_id: null, code: "LIBERO", description: "", quantity: 1, unit: "Kg",
      unit_price: 0, line_total: 0, is_free: true, vat_rate: 0.10,
    }]);
  };
  const updateLine = (idx, field, value) => {
    setCart((prev) => {
      const c = [...prev];
      c[idx] = { ...c[idx], [field]: value };
      c[idx].line_total = +(c[idx].quantity * c[idx].unit_price).toFixed(2);
      return c;
    });
  };
  const removeLine = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));

  // ---- Ordini storici (riordino rapido) ----
  const openHistory = async () => {
    if (!selectedCustomerId) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/customers/${selectedCustomerId}/ordered-items`);
      setHistoryData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento storico");
      setHistoryOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const addFromHistory = (h, { silent = false } = {}) => {
    const live = h.product_id ? catalog?.products?.find((p) => p.id === h.product_id) : null;
    const price = live ? live.price : (h.last_price || 0);
    const qty = h.last_quantity || 1;
    setCart((prev) => {
      const idx = h.product_id ? prev.findIndex((p) => p.product_id === h.product_id) : -1;
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], quantity: qty, line_total: +(qty * c[idx].unit_price).toFixed(2) };
        return c;
      }
      return [...prev, {
        product_id: h.product_id || null,
        code: h.code || (h.product_id ? "" : "LIBERO"),
        description: h.description || "",
        category_id: live?.category_id || h.category_id || null,
        category_name: live?.category_name || h.category_name || "",
        quantity: qty,
        unit: h.unit || "Kg",
        unit_price: price,
        line_total: +(qty * price).toFixed(2),
        is_free: !h.product_id,
        vat_rate: h.vat_rate ?? 0.10,
      }];
    });
    if (!silent) toast.success(`"${(h.description || "").slice(0, 40)}" aggiunto al carrello`);
  };

  const addAllFromHistory = () => {
    (historyData?.items || []).forEach((h) => addFromHistory(h, { silent: true }));
    toast.success(`${(historyData?.items || []).length} articoli aggiunti al carrello`);
    setHistoryOpen(false);
  };

  const subtotal = cart.reduce((s, it) => s + (it.line_total || 0), 0);
  const isForeign = !!selectedCustomer?.is_foreign;
  const vatTotal = isForeign ? 0 : cart.reduce((s, it) => s + (it.line_total * (it.vat_rate || 0)), 0);
  const grandTotal = subtotal + vatTotal;

  const resolveDeliveryAddress = () => {
    if (!selectedCustomer) return null;
    if (selectedDeliveryIdx === "__sede__") {
      return {
        label: "Sede legale",
        address: selectedCustomer.address || "",
        city: selectedCustomer.city || "",
        cap: selectedCustomer.cap || "",
        province: selectedCustomer.province || "",
        notes: "",
      };
    }
    if (selectedDeliveryIdx === "__new__") return { ...customDeliveryAddr };
    const idx = parseInt(selectedDeliveryIdx, 10);
    const da = (selectedCustomer.delivery_addresses || [])[idx];
    return da ? { ...da } : null;
  };

  const validateOrder = () => {
    if (!selectedCustomer) { toast.error("Seleziona un cliente"); return false; }
    if (cart.length === 0) { toast.error("Carrello vuoto"); return false; }
    if (cart.some((it) => !it.description || it.quantity <= 0)) { toast.error("Descrizione e quantità obbligatorie su ogni riga"); return false; }
    if (selectedDeliveryIdx === "__new__" && !customDeliveryAddr.address) {
      toast.error("Indirizzo di consegna obbligatorio"); return false;
    }
    return true;
  };

  const openRevision = () => {
    if (!validateOrder()) return;
    setShowRevision(true);
  };

  const submit = async (asDraft = false) => {
    if (!asDraft && exposure?.over_limit) {
      toast.error("ORDINE BLOCCATO — PLAFOND SUPERATO — SOLLECITARE PAGAMENTO");
      return;
    }
    setSubmitting(true);
    try {
      const delivery_address = resolveDeliveryAddress();
      const payload = {
        customer: {
          customer_name: selectedCustomer.business_name || "",
          company: selectedCustomer.business_name || "",
          vat_number: selectedCustomer.vat_number || "",
          fiscal_code: selectedCustomer.fiscal_code || "",
          sdi_code: selectedCustomer.sdi_code || "",
          pec: selectedCustomer.pec || "",
          email: selectedCustomer.email || "",
          phone: selectedCustomer.phone || "",
          address: selectedCustomer.address || "",
          city: selectedCustomer.city || "",
          cap: selectedCustomer.cap || "",
          province: selectedCustomer.province || "",
          is_foreign: !!selectedCustomer.is_foreign,
          special_requests: specialRequests,
        },
        items: cart,
        customer_id: selectedCustomer.id,
        pricelist_type_id: pricelistOverride || catalog?.pricelist?.id || null,
        set_pricelist_as_default: setAsDefault,
        delivery_address,
        delivery_notes: deliveryNotes,
        requested_shipping_date: requestedShippingDate || null,
        save_as_draft: asDraft,
        draft_order_id: draftId || null,
      };
      const r = await axios.post(`${API}/orders`, payload);
      if (asDraft) {
        toast.success(`Ordine ${r.data.order_number} salvato come provvisorio 📝`);
      } else {
        toast.success(`Ordine ${r.data.order_number} inviato!`);
      }
      setCart([]); setSpecialRequests(""); setDeliveryNotes(""); setRequestedShippingDate("");
      setPricelistOverride(""); setSetAsDefault(false); setDraftId(null); setDraftNumber(null);
      setSelectedDeliveryIdx("__sede__"); setCustomDeliveryAddr({ label: "", address: "", city: "", cap: "", province: "", notes: "" });
      setShowRevision(false);
      if (selectedDeliveryIdx === "__new__" && saveDeliveryToCustomer && customDeliveryAddr.address) {
        try {
          await axios.post(`${API}/customers/${selectedCustomer.id}/delivery-addresses`, customDeliveryAddr);
          toast.success("Indirizzo salvato in anagrafica");
        } catch { /* non bloccante */ }
      }
      navigate("/agent/orders");
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore invio ordine: controlla i dati del cliente");
    }
    finally { setSubmitting(false); }
  };

  const saveDraft = () => {
    if (!validateOrder()) return;
    submit(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Nuovo ordine</h1>
          <p className="text-stone-500 text-sm mt-1">Seleziona cliente e crea un ordine dal listino</p>
          {draftId && (
            <Badge className="mt-1 bg-stone-200 text-stone-700 border border-stone-300" data-testid="editing-draft-badge">
              📝 Stai modificando il provvisorio {draftNumber}
            </Badge>
          )}
        </div>
      </div>

      <Card className="bg-white border-stone-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-stone-700">Cliente *</Label>
            <CustomerSearchSelect
              customers={customers}
              value={selectedCustomerId}
              onChange={setSelectedCustomerId}
              placeholder="Cerca per nome, P.IVA, codice..."
            />
          </div>
          {selectedCustomer && (
            <div className="text-xs bg-stone-50 p-3 rounded border border-stone-200">
              <p><strong>{selectedCustomer.business_name}</strong> · P.IVA {selectedCustomer.vat_number}</p>
              <p>{selectedCustomer.address}, {selectedCustomer.city}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="order-pricelist-picker">
                <span className="text-stone-600 font-medium">Listino:</span>
                <Select value={pricelistOverride || catalog?.pricelist?.id || ""} onValueChange={(v) => setPricelistOverride(v)}>
                  <SelectTrigger className="h-8 w-64 bg-white text-xs border-stone-300" data-testid="order-pricelist-select">
                    <SelectValue placeholder="Listino" />
                  </SelectTrigger>
                  <SelectContent>
                    {(catalog?.available_pricelists || []).map((pl) => (
                      <SelectItem key={pl.id} value={pl.id}>
                        {pl.name}{pl.id === catalog?.customer_default_pricelist_id ? " · predefinito" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer select-none">
                  <input type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)} className="accent-[#8A1C2A]" data-testid="order-pricelist-set-default" />
                  Imposta come predefinito del cliente
                </label>
                {selectedCustomer.is_foreign && <Badge className="bg-blue-100 text-blue-700">IVA 0% estero</Badge>}
              </div>
              <Button size="sm" variant="outline" onClick={openHistory} className="mt-2 h-8 border-[#C5A059] text-[#8A1C2A] hover:bg-[#8A1C2A]/5" data-testid="order-history-btn">
                <History className="w-3.5 h-3.5 mr-1" /> Ordini storici
              </Button>
            </div>
          )}
        </div>
        {selectedCustomer && (
          <div className="mt-4 pt-4 border-t border-stone-200">
            <Label className="text-stone-700 flex items-center gap-1"><MapPin className="w-4 h-4 text-[#8A1C2A]" /> Indirizzo di consegna</Label>
            <Select value={selectedDeliveryIdx} onValueChange={setSelectedDeliveryIdx}>
              <SelectTrigger className="bg-white border-stone-300 mt-1" data-testid="delivery-address-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__sede__">Sede legale — {selectedCustomer.address}, {selectedCustomer.city}</SelectItem>
                {(selectedCustomer.delivery_addresses || []).map((d, i) => (
                  <SelectItem key={i} value={String(i)}>{d.label || `Indirizzo ${i + 1}`} — {d.address}, {d.city}</SelectItem>
                ))}
                <SelectItem value="__new__">+ Aggiungi nuovo indirizzo (una tantum)</SelectItem>
              </SelectContent>
            </Select>
            {selectedDeliveryIdx === "__new__" && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2 bg-amber-50 p-3 rounded border border-amber-200" data-testid="custom-delivery-form">
                <div className="md:col-span-3"><Label className="text-xs">{t.label}</Label><Input value={customDeliveryAddr.label} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, label: e.target.value })} className="bg-white border-stone-300 h-9" placeholder="Es. Cantiere" /></div>
                <div className="md:col-span-5"><Label className="text-xs">Indirizzo *</Label><Input value={customDeliveryAddr.address} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, address: e.target.value })} className="bg-white border-stone-300 h-9" data-testid="custom-delivery-address" /></div>
                <div className="md:col-span-2"><Label className="text-xs">{t.city}</Label><Input value={customDeliveryAddr.city} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, city: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
                <div className="md:col-span-1"><Label className="text-xs">CAP</Label><Input value={customDeliveryAddr.cap} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, cap: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
                <div className="md:col-span-1"><Label className="text-xs">Prov.</Label><Input value={customDeliveryAddr.province} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, province: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
                <div className="md:col-span-12"><Label className="text-xs">Note indirizzo</Label><Input value={customDeliveryAddr.notes} onChange={(e) => setCustomDeliveryAddr({ ...customDeliveryAddr, notes: e.target.value })} className="bg-white border-stone-300 h-9" placeholder="Riferimento, orari, citofono..." /></div>
                <label className="md:col-span-12 flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveDeliveryToCustomer}
                    onChange={(e) => setSaveDeliveryToCustomer(e.target.checked)}
                    className="accent-[#8A1C2A]"
                    data-testid="save-delivery-address-check"
                  />
                  <span><strong>Salva in anagrafica</strong> — questo indirizzo sarà disponibile anche per i prossimi ordini di {selectedCustomer.business_name}</span>
                </label>
              </div>
            )}
            {(() => { const da = resolveDeliveryAddress(); return <div className="mt-3"><DeliveryCard cap={da?.cap} localita={da?.city} simulate={3} compact testid="agent-delivery-card" /></div>; })()}
            <div className="mt-3">
              <Label className="text-xs">Note di consegna (specifiche per questo ordine)</Label>
              <div className="flex gap-2">
                <Input value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} className="bg-white border-stone-300 flex-1" placeholder="Es. consegnare al magazzino retro" data-testid="delivery-notes-input" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveQuickNote}
                  disabled={!deliveryNotes.trim()}
                  className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#8A1C2A]/5 shrink-0"
                  title="Salva come nota rapida riutilizzabile"
                  data-testid="save-quick-note-btn"
                >
                  <BookmarkPlus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Salva nota</span>
                </Button>
              </div>
              {quickNotes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="quick-notes-list">
                  <span className="text-[10px] text-stone-500 self-center">Note rapide:</span>
                  {quickNotes.map((n) => (
                    <span
                      key={n.id}
                      className="inline-flex items-center gap-1 bg-stone-100 hover:bg-[#8A1C2A]/10 border border-stone-200 rounded-full pl-3 pr-1 py-0.5 text-xs cursor-pointer transition-colors"
                      onClick={() => setDeliveryNotes(n.text)}
                      title={n.text}
                      data-testid={`quick-note-chip-${n.id}`}
                    >
                      {n.text.slice(0, 40)}{n.text.length > 40 ? "…" : ""}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteQuickNote(n.id); }}
                        className="p-0.5 rounded-full hover:bg-red-100 text-stone-400 hover:text-red-600"
                        title="Elimina nota rapida"
                        data-testid={`quick-note-delete-${n.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 max-w-xs">
              <Label className="text-xs">🚚 Data di consegna richiesta (facoltativa)</Label>
              <DatePickerIT value={requestedShippingDate} onChange={setRequestedShippingDate} data-testid="requested-shipping-date" />
              <p className="text-[10px] text-stone-500 mt-1">Sarà visibile al magazzino e sull'etichetta di riepilogo ordine.</p>
            </div>

          </div>
        )}
      </Card>

      {/* PLAFOND ASSICURATIVO */}
      {selectedCustomer && exposure && (exposure.insurance_status === "refused" || exposure.insurance_status === "revoked") && exposure.advance_payment_exempt && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 flex items-center gap-3 text-sm" data-testid="plafond-exempt-banner">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="font-bold text-amber-900">Plafond assicurativo NON CONCESSO — cliente esente dal pagamento anticipato</p>
            <p className="text-xs text-amber-800">Eccezione aziendale impostata da {exposure.advance_payment_exempt_by || "admin"}: l&apos;ordine non viene bloccato in magazzino.</p>
          </div>
        </div>
      )}
      {selectedCustomer && exposure && (exposure.insurance_status === "refused" || exposure.insurance_status === "revoked") && !exposure.advance_payment_exempt && (
        <div className="rounded-lg border-2 border-red-700 bg-red-600 text-white p-4 flex items-center gap-3 shadow-lg" data-testid="plafond-advance-banner">
          <AlertTriangle className="w-9 h-9 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-extrabold text-base sm:text-lg tracking-wide">⚠️ PAGAMENTO ANTICIPATO RICHIESTO</p>
            <p className="text-xs text-red-100 mt-1">Plafond assicurativo <b>NON CONCESSO</b> per questo cliente: incassare prima della consegna. L'ordine si può comunque inserire.</p>
          </div>
        </div>
      )}
      {selectedCustomer && exposure && exposure.insurance_status !== "refused" && exposure.insurance_status !== "revoked" && (exposure.credit_limit || 0) > 0 && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="plafond-info-panel">
          <span className="font-bold text-stone-900">Plafond assicurativo</span>
          <span>Concesso: <b className="text-emerald-700" data-testid="plafond-granted">{fm(exposure.credit_limit)}</b></span>
          <span>Utilizzato: <b data-testid="plafond-used">{fm(exposure.open_amount)}</b></span>
          <span>Disponibile: <b className={exposure.credit_limit - exposure.open_amount < 0 ? "text-red-600" : "text-emerald-700"} data-testid="plafond-available">{fm(exposure.credit_limit - exposure.open_amount)}</b></span>
        </div>
      )}
      {selectedCustomer && exposure && !exposure.insurance_status && (exposure.credit_limit || 0) === 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="plafond-missing-panel">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <span className="flex-1 min-w-[180px]"><b>Plafond da richiedere</b> — nessuna copertura assicurativa registrata per questo cliente.</span>
          {exposure.pending_request ? (
            <Badge className="bg-sky-100 text-sky-700">Richiesta inviata — in attesa</Badge>
          ) : (
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={() => setPlafondReqOpen(true)} data-testid="plafond-request-btn">
              Richiedi plafond
            </Button>
          )}
        </div>
      )}

      {/* BLOCCO PLAFOND: banner rosso ordine bloccato */}
      {selectedCustomer && exposure?.over_limit && (
        <div className="rounded-lg border-2 border-red-700 bg-red-600 text-white p-4 flex items-center gap-3 shadow-lg" data-testid="plafond-blocked-banner">
          <AlertTriangle className="w-9 h-9 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-extrabold text-base sm:text-lg tracking-wide">🚫 ORDINE BLOCCATO — PLAFOND SUPERATO — SOLLECITARE PAGAMENTO</p>
            <p className="text-xs text-red-100 mt-1">
              Plafond: <b>{fm(exposure.credit_limit)}</b> · Aperto: <b>{fm(exposure.open_amount)}</b> · Sforamento: <b>{fm(exposure.open_amount - exposure.credit_limit)}</b>
            </p>
          </div>
          <Button size="sm" onClick={goToCustomerStatements} className="bg-white text-red-700 hover:bg-red-50 font-semibold shrink-0" data-testid="plafond-go-statements-btn">
            <Receipt className="w-3.5 h-3.5 mr-1" /> Situazione cliente
          </Button>
        </div>
      )}

      {/* Alert situazione contabile del cliente (scaduto/aperto dallo scadenziario) */}
      {selectedCustomer && exposure && (exposure.overdue_amount > 0 || exposure.open_amount > 0) && (
        <div
          className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${exposure.overdue_amount > 0 ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}
          data-testid="customer-exposure-alert"
        >
          <AlertTriangle className={`w-6 h-6 shrink-0 ${exposure.overdue_amount > 0 ? "text-red-600" : "text-amber-600"}`} />
          <div className="flex-1 min-w-[200px] text-sm">
            <p className="font-semibold text-stone-900">
              Situazione contabile — {selectedCustomer.business_name}
            </p>
            <div className="text-xs text-stone-700 mt-0.5">
              {exposure.overdue_amount > 0 && (
                <>SCADUTO: <b className="text-red-700" data-testid="exposure-overdue">{fm(exposure.overdue_amount)}</b> · </>
              )}
              Aperto totale: <b data-testid="exposure-open">{fm(exposure.open_amount)}</b>
              {exposure.over_limit && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">FIDO SUPERATO</Badge>}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={goToCustomerStatements}
            className={exposure.overdue_amount > 0 ? "border-red-400 text-red-700 hover:bg-red-100" : "border-amber-400 text-amber-800 hover:bg-amber-100"}
            data-testid="go-to-customer-statements-btn"
          >
            <Receipt className="w-3.5 h-3.5 mr-1" /> Situazione cliente →
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {/* Catalog — full width */}
        <div>
          <Card className="bg-white border-stone-200 p-4">
            <div className="flex gap-2 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input placeholder="Cerca prodotto, codice o categoria..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="catalog-search" />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-36 sm:w-48 bg-white border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte categorie</SelectItem>
                  {catalog?.categories?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex border border-stone-300 rounded-md overflow-hidden shrink-0" role="group" aria-label="Modalità visualizzazione">
                <button type="button" onClick={() => setBigView(false)} className={`px-2.5 flex items-center justify-center ${!bigView ? "bg-[#8A1C2A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`} title="Vista compatta" data-testid="view-compact-btn">
                  <ListIcon className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setBigView(true)} className={`px-2.5 flex items-center justify-center border-l border-stone-300 ${bigView ? "bg-[#8A1C2A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`} title="Vista catalogo" data-testid="view-catalog-btn">
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="max-h-[65vh] overflow-y-auto">
              {bigView ? (
                /* ---- Vista CATALOGO: card grandi, comoda da mobile/tablet ---- */
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="catalog-big-grid">
                  {catalog?.products?.map((p) => (
                    <Card key={p.id} className="bg-white border-stone-200 hover:border-[#8A1C2A] transition-colors overflow-hidden flex flex-col" data-testid={`catalog-big-${p.code}`}>
                      <button type="button" onClick={() => setProductDetail(p)} className="w-full h-28 sm:h-40 bg-stone-100 flex items-center justify-center overflow-hidden" title="Vedi dettagli prodotto">
                        {p.image_path ? (
                          <img src={p.image_path.startsWith("http") ? p.image_path : `${BACKEND_URL}/api/files/${p.image_path}`} alt={p.description} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <ImageIcon className="w-10 h-10 text-stone-300" />
                        )}
                      </button>
                      <div className="p-2.5 flex-1 flex flex-col">
                        <p className="font-mono text-[10px] text-stone-500">{p.code}</p>
                        <p className="text-sm font-semibold leading-tight line-clamp-2" title={p.description}>{p.description}</p>
                        {p.category_name && <p className="text-[11px] font-bold text-[#8A1C2A] uppercase tracking-wide truncate" data-testid={`cat-big-${p.code}`}>{p.category_name}</p>}
                        <div className="mt-auto pt-1.5">
                          <p className="text-base sm:text-lg font-bold text-[#8A1C2A]">{p.price > 0 ? fm(p.price) : "—"}<span className="text-[10px] font-normal text-stone-500"> / {p.unit || "Kg"}</span></p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <Button size="sm" onClick={() => addToCart(p)} disabled={!p.price || p.price <= 0} className="flex-1 bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-9" data-testid={`add-cart-big-${p.code}`}>
                              <Plus className="w-4 h-4 mr-1" /> Aggiungi
                            </Button>
                            <TechSheetButton productId={p.id} variant="icon" />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {catalog?.products?.map((p) => (
                  <Card key={p.id} className="bg-white border-stone-200 hover:border-[#8A1C2A] transition-colors overflow-hidden" data-testid={`catalog-prod-${p.code}`}>
                    <div className="flex items-stretch gap-2">
                      <button
                        type="button"
                        onClick={() => setProductDetail(p)}
                        className="shrink-0 w-20 h-20 bg-stone-100 flex items-center justify-center overflow-hidden"
                        title="Vedi dettagli prodotto"
                      >
                        {p.image_path ? (
                          <img
                            src={p.image_path.startsWith("http") ? p.image_path : `${BACKEND_URL}/api/files/${p.image_path}`}
                            alt={p.description}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <ImageIcon className="w-7 h-7 text-stone-300" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <p className="font-mono text-[10px] text-stone-500">{p.code}</p>
                        <p className="text-sm font-medium leading-tight truncate" title={p.description}>{p.description}</p>
                        {p.category_name && <p className="text-[11px] font-bold text-[#8A1C2A] uppercase tracking-wide truncate" data-testid={`cat-list-${p.code}`}>{p.category_name}</p>}
                        <div className="flex items-center justify-between mt-1">
                          <span className="font-semibold text-[#8A1C2A] text-sm">{p.price > 0 ? `${fm(p.price)}` : "—"}<span className="text-[10px] text-stone-500"> / {p.unit || "Kg"}</span></span>
                          <div className="flex items-center gap-1">
                            <TechSheetButton productId={p.id} variant="icon" />
                            <Button size="icon" variant="ghost" onClick={() => setProductDetail(p)} className="h-7 w-7" title="Dettagli">
                              <Eye className="w-3 h-3 text-stone-500" />
                            </Button>
                            <Button size="sm" onClick={() => addToCart(p)} disabled={!p.price || p.price <= 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-7" data-testid={`add-cart-${p.code}`}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              )}
              {(!catalog?.products || catalog.products.length === 0) && <p className="p-4 text-center text-stone-500 text-sm">{t.pAgent_noProductFound}</p>}
            </div>
          </Card>
        </div>
        {/* Cart — full width below the catalog */}
        <Card className="bg-white border-stone-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-stone-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-[#8A1C2A]" /> Carrello</h3>
            <Button size="sm" variant="outline" onClick={addFreeItem} className="border-[#C5A059] text-[#8A1C2A]" data-testid="add-free-item-btn">
              <Plus className="w-3 h-3 mr-1" /> Articolo libero
            </Button>
          </div>
          {cart.length === 0 ? (
            <p className="py-8 text-center text-stone-400 text-sm">Vuoto. Aggiungi prodotti dal listino o crea un articolo libero.</p>
          ) : (
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {cart.map((it, idx) => (
                <div key={idx} className={`p-2.5 border rounded ${it.is_free ? "bg-amber-50 border-amber-200" : "border-stone-200"}`} data-testid={`cart-line-${idx}`}>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[220px] self-center">
                      {it.is_free ? (
                        <Input value={it.description} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Descrizione articolo libero..." className="text-sm h-9 bg-white border-amber-300" data-testid={`free-desc-${idx}`} />
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-stone-500 font-mono truncate">{it.code}</p>
                            {it.category_name && <span className="text-[10px] text-[#8A1C2A] bg-[#8A1C2A]/10 px-1.5 py-0.5 rounded font-medium truncate max-w-[160px]" title={it.category_name}>{it.category_name}</span>}
                          </div>
                          <p className="text-sm font-medium truncate">{it.description}</p>
                        </>
                      )}
                    </div>
                    <div>
                      <span className="block text-[9px] uppercase text-stone-400 mb-0.5">Q.tà</span>
                      <DecimalInput value={it.quantity} onValue={(n) => updateLine(idx, "quantity", n)} className="w-24 h-9 text-sm text-right bg-white border-stone-300" data-testid={`cart-qty-${idx}`} />
                    </div>
                    <div>
                      <span className="block text-[9px] uppercase text-stone-400 mb-0.5">U.M.</span>
                      <Select value={it.unit} onValueChange={(v) => updateLine(idx, "unit", v)}>
                        <SelectTrigger className="w-20 h-9 text-sm bg-white border-stone-300"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kg">Kg</SelectItem>
                          <SelectItem value="Pz">Pz</SelectItem>
                          <SelectItem value="CT">CT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <span className="block text-[9px] uppercase text-stone-400 mb-0.5">€/UM</span>
                      <DecimalInput value={it.unit_price} onValue={(n) => updateLine(idx, "unit_price", n)} placeholder="—" className="w-28 h-9 text-sm text-right bg-white border-stone-300" data-testid={`cart-price-${idx}`} />
                    </div>
                    <div className="w-24 text-right">
                      <span className="block text-[9px] uppercase text-stone-400 mb-0.5">Totale</span>
                      <span className="text-sm font-semibold text-[#8A1C2A] leading-9">{fm((it.line_total || 0))}</span>
                    </div>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => removeLine(idx)}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-stone-200 mt-3 pt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Note / richieste speciali</Label>
              <textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={3} placeholder="Note libere per il magazzino..." className="w-full mt-1 px-3 py-2 text-xs bg-white border border-stone-300 rounded-md" data-testid="new-order-notes" />
            </div>
            <div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Imponibile</span><span className="font-semibold">{fm(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-stone-600">IVA {isForeign ? "(estero 0%)" : ""}</span><span className="font-semibold">{fm(vatTotal)}</span></div>
                <div className="flex justify-between text-lg pt-1 border-t border-stone-200"><span className="font-semibold">Totale</span><span className="font-bold text-[#8A1C2A]">{fm(grandTotal)}</span></div>
              </div>
              <Button onClick={openRevision} disabled={submitting || cart.length === 0 || !selectedCustomer || exposure?.over_limit} className="w-full mt-3 bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="submit-new-order-btn">
                {exposure?.over_limit ? "🚫 ORDINE BLOCCATO — PLAFOND" : <>Rivedi e conferma — {fm(grandTotal)}</>}
              </Button>
              <Button
                variant="outline"
                onClick={saveDraft}
                disabled={submitting || cart.length === 0 || !selectedCustomer}
                className="w-full mt-2 border-stone-400 text-stone-700 hover:bg-stone-100"
                data-testid="save-draft-btn"
              >
                📝 {draftId ? "Aggiorna provvisorio" : "Salva come provvisorio"} (non inviato al magazzino)
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Revision step */}
      <Dialog open={showRevision} onOpenChange={(o) => !o && setShowRevision(false)}>
        <DialogContent className="max-w-3xl bg-white max-h-[95vh] overflow-y-auto" data-testid="revision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#8A1C2A]" /> Conferma riepilogo ordine</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-3">
              <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
                <p className="font-semibold">{selectedCustomer.business_name}</p>
                <p className="text-xs text-stone-600">P.IVA {selectedCustomer.vat_number} · {selectedCustomer.email} · {selectedCustomer.phone}</p>
                {(() => {
                  const da = resolveDeliveryAddress();
                  return da ? (
                    <div className="mt-2 pt-2 border-t border-stone-200">
                      <p className="text-xs font-semibold text-stone-700">📍 Indirizzo consegna {da.label && <span className="text-[#8A1C2A]">({da.label})</span>}</p>
                      <p className="text-xs">{da.address}, {da.city} {da.cap} {da.province && `(${da.province})`}</p>
                      {da.notes && <p className="text-xs italic text-stone-600">Note indirizzo: {da.notes}</p>}
                      {deliveryNotes && <p className="text-xs italic text-amber-700">📝 Note ordine: {deliveryNotes}</p>}
                      {requestedShippingDate && <p className="text-xs font-semibold text-amber-700">🚚 Consegna richiesta: {new Date(requestedShippingDate).toLocaleDateString("it-IT")}</p>}
                    </div>
                  ) : null;
                })()}
              </Card>
              <Card className="bg-white border-stone-200 overflow-hidden">
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-stone-100">
                      <tr><th className="text-left p-2 text-xs">Codice</th><th className="text-left p-2 text-xs">{t.pCust_filterCategory}</th><th className="text-left p-2 text-xs">Descrizione</th><th className="text-right p-2 text-xs">Q.tà</th><th className="text-right p-2 text-xs">€/UM</th><th className="text-right p-2 text-xs">Totale</th></tr>
                    </thead>
                    <tbody>
                      {cart.map((it, idx) => (
                        <tr key={idx} className="border-t border-stone-100">
                          <td className="p-2 font-mono text-xs">{it.code}</td>
                          <td className="p-2 text-xs text-stone-600">{it.category_name || "—"}</td>
                          <td className="p-2">{it.description}</td>
                          <td className="p-2 text-right">{it.quantity} {it.unit}</td>
                          <td className="p-2 text-right">{fm(it.unit_price)}</td>
                          <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm(it.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50"><td colSpan="5" className="p-2 text-right">Imponibile</td><td className="p-2 text-right font-semibold">{fm(subtotal)}</td></tr>
                      <tr><td colSpan="5" className="p-2 text-right text-xs">IVA {isForeign ? "(estero 0%)" : ""}</td><td className="p-2 text-right text-xs">{fm(vatTotal)}</td></tr>
                      <tr className="border-t border-stone-200 bg-amber-50"><td colSpan="5" className="p-2 text-right font-bold">Totale</td><td className="p-2 text-right text-lg font-bold text-[#8A1C2A]">{fm(grandTotal)}</td></tr>
                    </tfoot>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100" data-testid="agent-revision-items-mobile">
                  {cart.map((it, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-mono text-stone-500">{it.code} {it.category_name && <span className="ml-1 text-stone-400">· {it.category_name}</span>}</p>
                          <p className="text-sm font-medium leading-tight">{it.description}</p>
                        </div>
                        <p className="font-semibold text-[#8A1C2A] text-sm whitespace-nowrap">{fm(it.line_total)}</p>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-stone-600">
                        <span>{it.quantity} {it.unit}</span>
                        <span>{fm(it.unit_price)} / {it.unit || "Kg"}</span>
                      </div>
                    </div>
                  ))}
                  <div className="p-3 bg-stone-50 text-sm space-y-1">
                    <div className="flex justify-between"><span>Imponibile</span><span className="font-semibold">{fm(subtotal)}</span></div>
                    <div className="flex justify-between text-xs"><span>IVA {isForeign ? "(estero 0%)" : ""}</span><span>{fm(vatTotal)}</span></div>
                    <div className="flex justify-between border-t border-stone-200 pt-1 mt-1 bg-amber-50 -mx-3 px-3 py-1"><span className="font-bold">Totale</span><span className="font-bold text-lg text-[#8A1C2A]">{fm(grandTotal)}</span></div>
                  </div>
                </div>
              </Card>
              {specialRequests && (
                <Card className="bg-yellow-50 border-yellow-200 p-3 text-sm">
                  <p className="text-xs font-semibold text-yellow-900 mb-1">{t.pAgent_specialRequests}</p>
                  <p className="text-xs whitespace-pre-wrap">{specialRequests}</p>
                </Card>
              )}
              <DialogFooter className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowRevision(false)} className="border-stone-300" data-testid="revision-back-btn">← Torna indietro</Button>
                <Button variant="outline" onClick={() => submit(true)} disabled={submitting} className="border-stone-400 text-stone-700 hover:bg-stone-100" data-testid="revision-save-draft-btn">
                  📝 Salva provvisorio
                </Button>
                <Button onClick={() => submit(false)} disabled={submitting || exposure?.over_limit} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="revision-confirm-btn">
                  <Check className="w-4 h-4 mr-1" /> {submitting ? "Invio..." : `Conferma e invia — ${fm(grandTotal)}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ordini storici — riordino rapido */}
      <Dialog open={historyOpen} onOpenChange={(o) => !o && setHistoryOpen(false)}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="order-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#8A1C2A]" /> Ordini storici — {selectedCustomer?.business_name}
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <p className="py-10 text-center text-stone-400 text-sm">Caricamento storico…</p>
          ) : !historyData || historyData.items.length === 0 ? (
            <p className="py-10 text-center text-stone-400 text-sm" data-testid="order-history-empty">Nessun ordine precedente per questo cliente.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-stone-500">{historyData.orders_count} ordini · {historyData.items.length} articoli diversi — tocca <strong>+</strong> per aggiungere con l'ultima quantità</p>
                <Button size="sm" onClick={addAllFromHistory} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-8" data-testid="history-add-all-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi tutto
                </Button>
              </div>
              <div className="divide-y divide-stone-100 border border-stone-200 rounded-md overflow-hidden">
                {historyData.items.map((h, i) => {
                  const live = h.product_id ? catalog?.products?.find((p) => p.id === h.product_id) : null;
                  const priceNow = live ? live.price : h.last_price;
                  return (
                    <div key={i} className="p-2.5 flex items-center gap-2 hover:bg-stone-50" data-testid={`history-item-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono text-stone-500">{h.code}{h.category_name && <span className="ml-1 text-stone-400">· {h.category_name}</span>}</p>
                        <p className="text-sm font-medium leading-tight truncate" title={h.description}>{h.description}</p>
                        <p className="text-[11px] text-stone-500 mt-0.5">
                          Ordinato <strong>{h.times_ordered}×</strong> · ultima volta {h.last_quantity} {h.unit} il {(h.last_ordered_at || "").slice(0, 10)}
                          {live && live.price !== h.last_price && <span className="ml-1 text-amber-700">(prezzo aggiornato: {fm(live.price)})</span>}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-[#8A1C2A]">{fm(priceNow)}<span className="text-[10px] font-normal text-stone-500">/{h.unit}</span></p>
                        <Button size="sm" onClick={() => addFromHistory(h)} className="mt-1 h-8 bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid={`history-add-${i}`}>
                          <Plus className="w-3.5 h-3.5 mr-0.5" /> {h.last_quantity} {h.unit}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product detail modal */}
      <Dialog open={!!productDetail} onOpenChange={() => setProductDetail(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="product-detail-modal">
          {productDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{productDetail.description}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  {productDetail.image_path ? (
                    <img
                      src={productDetail.image_path.startsWith("http") ? productDetail.image_path : `${BACKEND_URL}/api/files/${productDetail.image_path}`}
                      alt={productDetail.description}
                      className="w-full h-64 object-cover rounded-lg border border-stone-200"
                    />
                  ) : (
                    <div className="w-full h-64 bg-stone-100 rounded-lg flex items-center justify-center border border-stone-200">
                      <ImageIcon className="w-16 h-16 text-stone-300" />
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div><p className="text-xs text-stone-500 font-mono">Codice</p><p className="font-mono font-semibold">{productDetail.code}</p></div>
                  <div><p className="text-xs text-stone-500">{t.pCust_filterCategory}</p><p className="text-sm">{productDetail.category_name}</p></div>
                  {productDetail.description_en && <div><p className="text-xs text-stone-500">English description</p><p className="text-sm italic text-stone-700">{productDetail.description_en}</p></div>}
                  {productDetail.note && <div><p className="text-xs text-stone-500">Note</p><p className="text-sm">{productDetail.note}</p></div>}
                  <div><p className="text-xs text-stone-500">Prezzo</p><p className="text-3xl font-bold text-[#8A1C2A]">{productDetail.price > 0 ? `${fm(productDetail.price)}` : "—"}<span className="text-sm font-normal text-stone-500"> / {productDetail.unit || "Kg"}</span></p></div>
                  <div><p className="text-xs text-stone-500">Disponibilità</p>{productDetail.availability === "DISPONIBILE" ? <Badge className="bg-green-100 text-green-700">Disponibile</Badge> : <Badge className="bg-yellow-100 text-yellow-700">{productDetail.availability || "-"}</Badge>}</div>
                  <Button onClick={() => { addToCart(productDetail); setProductDetail(null); }} disabled={!productDetail.price} className="w-full bg-[#8A1C2A] hover:bg-[#6B1420] text-white mt-2" data-testid="modal-add-to-cart-btn">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Aggiungi al carrello
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <PlafondRequestDialog
        customer={selectedCustomer}
        open={plafondReqOpen}
        onClose={() => setPlafondReqOpen(false)}
        onDone={() => axios.get(`${API}/sales-documents/customer-exposure/${selectedCustomerId}`).then((r) => setExposure(r.data)).catch(() => {})}
      />
    </div>
  );
};

export default NewOrderPage;
